import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { dashboard as dashboardApi } from "../../api/resources";
import { useAuth } from "../../lib/AuthContext";
import { useOrganizations } from "../../lib/OrganizationContext";
import type { OrganizationDashboard } from "../../lib/types";
import { percent } from "../../lib/format";

interface NavItem {
  to: string;
  label: string;
  badge?: keyof OrganizationDashboard;
  /** Hidden unless the signed-in user holds this permission in the current organization. */
  permission?: string;
}

// Items are gated by permission, not by role name, so a future role gets the right menu for free.
// A contributor sees My tasks, their interviews and uploads — never the operator's toolkit.
const NAV: Array<{ section: string; items: NavItem[] }> = [
  {
    section: "",
    items: [{ to: "/tasks", label: "My tasks", permission: "tasks:read" }],
  },
  {
    section: "Discovery",
    items: [
      { to: "/", label: "Dashboard", permission: "knowledge:read" },
      {
        to: "/onboarding",
        label: "Onboarding",
        permission: "onboarding:write",
      },
      {
        to: "/interviews",
        label: "Interviews",
        permission: "interview:respond",
      },
      {
        to: "/uploads",
        label: "Uploads",
        badge: "files_uploaded",
        permission: "upload:write",
      },
    ],
  },
  {
    section: "Context",
    items: [
      {
        to: "/knowledge",
        label: "What we learned",
        badge: "unverified_facts",
        permission: "knowledge:read",
      },
      {
        to: "/gaps",
        label: "Gaps & conflicts",
        badge: "knowledge_gaps",
        permission: "knowledge:read",
      },
      {
        to: "/data-dictionary",
        label: "Data Dictionary",
        permission: "registry:read",
      },
    ],
  },
  {
    section: "Registries",
    items: [
      {
        to: "/systems",
        label: "Systems",
        badge: "systems_discovered",
        permission: "registry:read",
      },
      {
        to: "/workflows",
        label: "Workflows",
        badge: "workflows_discovered",
        permission: "registry:read",
      },
      {
        to: "/integrations",
        label: "Integrations",
        badge: "integrations_discovered",
        permission: "registry:read",
      },
    ],
  },
  {
    section: "Administration",
    items: [
      {
        to: "/question-library",
        label: "Question & discovery packs",
        permission: "library:write",
      },
      { to: "/team", label: "Team & invitations", permission: "members:read" },
      { to: "/ai", label: "AI & cost", permission: "ai:read" },
      { to: "/settings", label: "Settings", permission: "org:write" },
    ],
  },
];

export function AppShell() {
  const { organizations, current, select, loading } = useOrganizations();
  const { user, signOut, roleIn, authRequired, can } = useAuth();
  const [counts, setCounts] = useState<OrganizationDashboard | undefined>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!current) {
      setCounts(undefined);
      return;
    }
    let cancelled = false;
    dashboardApi
      .organization(current.id)
      .then((data) => {
        if (!cancelled) setCounts(data);
      })
      .catch(() => {
        if (!cancelled) setCounts(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [current]);

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <strong>Discovery</strong>
          <span>How the company works</span>
        </div>
        <nav className="sidebar__nav">
          {NAV.map((group) => {
            // With enforcement off (or no organization yet) everything shows, as before.
            const items = group.items.filter(
              (item) =>
                !item.permission ||
                !authRequired ||
                !current ||
                can(current.id, item.permission),
            );
            if (items.length === 0) return null;
            return (
              <div key={group.section || "root"}>
                {group.section ? (
                  <div className="sidebar__section">{group.section}</div>
                ) : null}
                {items.map((item) => {
                  const badgeValue =
                    item.badge && counts ? Number(counts[item.badge] ?? 0) : 0;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        `sidebar__link${isActive ? " is-active" : ""}`
                      }
                    >
                      <span>{item.label}</span>
                      {badgeValue > 0 ? (
                        <span className="sidebar__badge">{badgeValue}</span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="sidebar__footer">
          {current ? (
            <>
              {current.dba_name ?? current.legal_name}
              <br />
              {percent(current.completion_percentage)} complete
            </>
          ) : (
            "No organization selected"
          )}
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <span className="topbar__title">
            {current
              ? (current.dba_name ?? current.legal_name)
              : "Discovery"}
          </span>
          <div className="topbar__spacer" />
          {organizations.length > 0 ? (
            <select
              aria-label="Current organization"
              style={{ width: 260 }}
              value={current?.id ?? ""}
              onChange={(event) => select(event.target.value)}
            >
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.dba_name ?? organization.legal_name}
                </option>
              ))}
            </select>
          ) : null}
          {!authRequired || !current || can(current.id, "onboarding:write") ? (
            <button
              className="primary"
              onClick={() => navigate("/onboarding/new")}
            >
              Start discovery
            </button>
          ) : null}
          {user ? (
            <div className="topbar__user">
              <div>
                <div className="small">{user.full_name ?? user.email}</div>
                <div className="tiny faint">
                  {user.is_superuser
                    ? "platform"
                    : (roleIn(current?.id) ?? "no role here")}
                </div>
              </div>
              <button
                className="btn--ghost"
                onClick={() => {
                  signOut();
                  navigate("/signin", { replace: true });
                }}
              >
                Sign out
              </button>
            </div>
          ) : !authRequired ? (
            <span
              className="badge badge--disputed"
              title="AUTH_REQUIRED is false on the API"
            >
              access control off
            </span>
          ) : null}
        </header>
        <main className="content">
          {loading && organizations.length === 0 ? (
            <div className="empty muted">Loading organizations…</div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
